import { s16 } from '@ardoq/design-tokens';
import { QueryBuilderText } from '../QueryBuilderTextInput';
import { noop } from 'lodash';
import { Fragment } from 'react';
import { Select, TextInputWithAutocomplete } from '@ardoq/select';
import styled from 'styled-components';
import { Locale } from '@ardoq/locale';
import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  GetRuleNotificationParams,
  InternalRule,
} from '../../types';
import {
  QueryBuilderFilterDefinition,
  QueryBuilderFilterDefinitionGroup,
  QueryBuilderFilterType,
} from '../../filterUtils/types';
import {
  Flex,
  QueryBuilderRuleInputsWrapper,
  QueryBuilderRuleNotificationMessage,
  QueryBuilderRuleWrapper,
} from '../atoms';
import { RuleValueInput } from './RuleValueInput';
import { findFilterDefinitionByName } from '../../filterUtils';
import { SelectOperator } from './SelectOperator';
import {
  getAsyncOptionsLoaderByAsyncFilterType,
  getDefaultOperator,
  getDefaultValue,
  getOperatorsLabel,
  getSelectFieldOptions,
  isFieldDisabled,
  maybeConvertValueForOperator,
} from './utils';
import { IconButton, DragButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import { TextInput } from '@ardoq/forms';
import { APIEntityType } from '@ardoq/api-types';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { ReducerActions } from '../../stateUtils/types';
import {
  isHasReferenceFilterId,
  isInternalRuleValid,
  isReferenceTypeFilterId,
} from '../../stateUtils/utils';

type RuleRowProps = {
  rule: InternalRule;
  reducerActions: ReducerActions;
  filterGroups: QueryBuilderFilterDefinitionGroup[];
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  showErrors?: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  clickIdPrefix?: string;
  isDraggable: boolean;
  getRuleNotificationParams?: GetRuleNotificationParams;
  isCompact?: boolean;
  locale: Locale;
  i18nLocale?: I18nLocale;
  menuPortalTarget?: HTMLElement | null;
};

const ReadOnlyRule = styled.div`
  margin-left: ${s16};
  display: flex;
`;

const MINIMAL_OPERATORS_INPUT_WIDTH = 176;
const HAS_REFERENCE_OPERATOR_INPUT_WIDTH = 96;

const isFilterReadOnly = (
  filterDefinition: QueryBuilderFilterDefinition | undefined
): filterDefinition is QueryBuilderFilterDefinition => {
  if (!filterDefinition) {
    return false;
  }

  return [
    QueryBuilderFilterType.READONLY,
    QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID,
  ].includes(filterDefinition.filterType);
};

export const RuleRow = ({
  rule,
  reducerActions,
  filterGroups,
  asyncSuggestionsLoaders,
  asyncLabelLoaders,
  showErrors,
  isReadOnly,
  isDisabled,
  clickIdPrefix,
  isDraggable,
  getRuleNotificationParams,
  isCompact,
  locale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  menuPortalTarget,
}: RuleRowProps) => {
  const isValid = isInternalRuleValid(rule);
  const filter = findFilterDefinitionByName(filterGroups, rule.field);
  const { operators, filterType } = filter || {};

  const isReadOnlyFilter = isFilterReadOnly(filter);
  const isHasReferenceFilter = isHasReferenceFilterId(filter?.id);
  const isReferenceTypeFilter = isReferenceTypeFilterId(filter?.id);

  if (rule.field && !filter) {
    const ruleNotificationParams = getRuleNotificationParams?.(rule);
    return (
      <QueryBuilderRuleWrapper
        $hasError
        $notificationType={ruleNotificationParams?.notificationType}
        $isReadOnly={isReadOnly}
        $isCompact={isCompact}
        // we pass down data-testid for React Testing Library selectors and data-test-id for e2e tests
        data-testid={`filter-rule-by-${rule.field}`}
        data-test-id="rule-row-wrapper"
        aria-label="filter-rule-row"
      >
        <QueryBuilderRuleNotificationMessage
          $notificationType={ruleNotificationParams?.notificationType}
        >
          {ruleNotificationParams?.message ||
            t(
              i18nLocale,
              'queryBuilder:rule.fieldDoesNotExist',
              { field: rule.field },
              'Field "{{field}}" does not exist.'
            )}
        </QueryBuilderRuleNotificationMessage>
        {!isReadOnly && (
          <IconButton
            label={t(i18nLocale, 'queryBuilder:button.removeRow', 'Remove row')}
            iconName={IconName.CLOSE}
            onClick={() => reducerActions.removeRow({ uuid: rule.uuid })}
            dataTestId="remove-row-button"
            isDisabled={isDisabled}
          />
        )}
      </QueryBuilderRuleWrapper>
    );
  }
  if (isReadOnly) {
    const filterLabel = filter?.label ?? '';
    const operatorLabel =
      (rule.operator &&
        getOperatorsLabel(rule.operator, i18nLocale, filterType)) ??
      '';

    return (
      <QueryBuilderRuleWrapper
        $hasError={showErrors && !isValid}
        $isCompact={isCompact}
        $isReadOnly
        // we pass down data-testid for React Testing Library selectors and data-test-id for e2e tests
        data-test-id="rule-row-wrapper"
        data-testid={`filter-rule-by-${rule.field}`}
        aria-label="filter-rule-row"
      >
        <ReadOnlyRule>
          <QueryBuilderText value={`${filterLabel} ${operatorLabel} `} />
          {filterType && rule.operator && (
            <RuleValueInput
              isQueryBuilderInReadOnlyMode
              key={rule.field}
              operator={rule.operator}
              asyncSuggestionsLoaders={asyncSuggestionsLoaders}
              asyncLabelLoaders={asyncLabelLoaders}
              filterType={filterType}
              filter={filter as QueryBuilderFilterDefinition}
              value={rule.value}
              onValueChange={noop}
              locale={locale}
              i18nLocale={i18nLocale}
              menuPortalTarget={menuPortalTarget}
            />
          )}
        </ReadOnlyRule>
      </QueryBuilderRuleWrapper>
    );
  }

  const InputsWrapper = isCompact ? QueryBuilderRuleInputsWrapper : Fragment;

  const ruleWrapper = (
    <QueryBuilderRuleWrapper
      $hasError={showErrors && !isValid}
      $isCompact={isCompact}
      // we pass down data-testid for React Testing Library selectors and data-test-id for e2e tests
      data-test-id="rule-row-wrapper"
      data-testid={`filter-rule-by-${rule.field}`}
      aria-label="filter-rule-row"
    >
      {!isReadOnly && (
        <DragButton
          draggable={isDraggable}
          style={{
            marginTop: isCompact ? '4px' : undefined,
            opacity: isDraggable ? 1 : 0.5,
          }}
          i18nLocale={i18nLocale}
        />
      )}
      <InputsWrapper
        {...(InputsWrapper !== Fragment
          ? { ['data-testid']: 'vertical-inputs-wrapper' }
          : undefined)}
      >
        <Flex flex={2}>
          {isReadOnlyFilter ? (
            <TextInput
              isReadOnly
              value={filter.label}
              isDisabled={isDisabled}
            />
          ) : (
            <Select
              aria-label={t(
                i18nLocale,
                'queryBuilder:ariaLabel.filterBy',
                'Filter by'
              )}
              placeholder={t(
                i18nLocale,
                'queryBuilder:placeholder.selectField',
                'Select what to search for...'
              )}
              value={rule.field}
              onValueChange={selectedFieldName => {
                const field = (selectedFieldName as string) || undefined;
                const filter = findFilterDefinitionByName(filterGroups, field);
                reducerActions.updateRow(rule.uuid, {
                  field,
                  operator: getDefaultOperator(filter),
                  value: getDefaultValue(filter),
                });
              }}
              options={getSelectFieldOptions(filterGroups)}
              clickId={
                clickIdPrefix ? `${clickIdPrefix}select-field` : undefined
              }
              dataTestId="select-field"
              isDisabled={isDisabled}
              hasError={isFieldDisabled(filterGroups, rule.field)}
              menuPortalTarget={menuPortalTarget}
            />
          )}
        </Flex>
        {isHasReferenceFilter && (
          <Flex flex={2}>
            <TextInputWithAutocomplete
              preloadOptionsOnFocus
              aria-label={t(
                i18nLocale,
                'queryBuilder:ariaLabel.referenceTypeName',
                'Reference type name'
              )}
              placeholder={t(
                i18nLocale,
                'queryBuilder:placeholder.anyReferenceType',
                'Any reference type'
              )}
              value={rule.referenceTypeName || ''}
              debounceTime={300}
              onValueChange={referenceTypeName => {
                if (
                  (referenceTypeName ?? '') === (rule.referenceTypeName ?? '')
                ) {
                  return;
                }
                reducerActions.updateRow(rule.uuid, {
                  referenceTypeName,
                });
              }}
              loadOptions={async inputValue =>
                (
                  await getAsyncOptionsLoaderByAsyncFilterType(
                    asyncSuggestionsLoaders,
                    QueryBuilderFilterType.ASYNC_REFERENCE_TYPE_NAME
                  )(inputValue)
                ).map(({ label }) => label)
              }
              dataTestId="reference-type-name-input"
              isDisabled={isDisabled}
              menuPortalTarget={menuPortalTarget}
            />
          </Flex>
        )}
        {operators && !isReferenceTypeFilter && (
          <Flex
            width={
              isCompact
                ? undefined
                : isHasReferenceFilter
                  ? HAS_REFERENCE_OPERATOR_INPUT_WIDTH
                  : MINIMAL_OPERATORS_INPUT_WIDTH
            }
          >
            {isReadOnlyFilter ? (
              <TextInput
                value={
                  rule.operator
                    ? getOperatorsLabel(rule.operator, i18nLocale)
                    : t(
                        i18nLocale,
                        'queryBuilder:rule.missingOperator',
                        'missing operator'
                      )
                }
                isReadOnly
                isDisabled={isDisabled}
              />
            ) : (
              <SelectOperator
                key={rule.operator}
                selectedOperator={rule.operator}
                filterType={filterType}
                operators={operators}
                onValueChange={operator => {
                  reducerActions.updateRow(rule.uuid, {
                    operator,
                    ...maybeConvertValueForOperator(
                      rule.value,
                      operator,
                      rule.operator,
                      filterType
                    ),
                  });
                }}
                clickId={
                  clickIdPrefix ? `${clickIdPrefix}select-operator` : undefined
                }
                isDisabled={isDisabled}
                i18nLocale={i18nLocale}
                menuPortalTarget={menuPortalTarget}
              />
            )}
          </Flex>
        )}
        {isHasReferenceFilter && (
          <Flex width={!isCompact ? MINIMAL_OPERATORS_INPUT_WIDTH : undefined}>
            <Select
              aria-label={t(
                i18nLocale,
                'queryBuilder:ariaLabel.targetEntityType',
                'Target entity type'
              )}
              value={
                rule.targetEntityType
                  ? rule.targetEntityType
                  : filter?.filterType ===
                      QueryBuilderFilterType.ASYNC_COMPONENT_NAME
                    ? APIEntityType.COMPONENT
                    : APIEntityType.COMPONENT_TYPE
              }
              options={[
                {
                  value: APIEntityType.COMPONENT,
                  label: t(
                    i18nLocale,
                    'queryBuilder:entityType.component',
                    'component'
                  ),
                },
                {
                  value: APIEntityType.COMPONENT_TYPE,
                  label: t(
                    i18nLocale,
                    'queryBuilder:entityType.componentType',
                    'component type'
                  ),
                },
              ]}
              onValueChange={targetEntityType => {
                reducerActions.updateRow(rule.uuid, {
                  targetEntityType: targetEntityType as
                    | APIEntityType.COMPONENT_TYPE
                    | APIEntityType.COMPONENT,
                });
              }}
              isDisabled={isDisabled}
              menuPortalTarget={menuPortalTarget}
            />
          </Flex>
        )}
        {filterType && rule.operator && (
          <RuleValueInput
            key={rule.field}
            operator={rule.operator}
            asyncSuggestionsLoaders={asyncSuggestionsLoaders}
            asyncLabelLoaders={asyncLabelLoaders}
            filterType={
              isHasReferenceFilter &&
              rule.targetEntityType === APIEntityType.COMPONENT_TYPE
                ? QueryBuilderFilterType.ASYNC_REFERENCED_COMPONENT_TYPE_NAME
                : filterType
            }
            filter={filter as QueryBuilderFilterDefinition}
            value={rule.value}
            onValueChange={newValue => {
              reducerActions.updateRow(rule.uuid, { value: newValue });
            }}
            clickId={clickIdPrefix ? `${clickIdPrefix}select-value` : undefined}
            isDisabled={isDisabled}
            locale={locale}
            i18nLocale={i18nLocale}
            menuPortalTarget={menuPortalTarget}
          />
        )}
      </InputsWrapper>
      {!isReadOnly && (
        <IconButton
          label={t(i18nLocale, 'queryBuilder:button.removeRow', 'Remove row')}
          iconName={IconName.CLOSE}
          onClick={() => reducerActions.removeRow({ uuid: rule.uuid })}
          dataTestId="remove-row"
          isDisabled={isDisabled}
        />
      )}
    </QueryBuilderRuleWrapper>
  );

  if (!isReadOnlyFilter) {
    return ruleWrapper;
  }

  return (
    <WithUncontrolledPopover
      content={
        <PlainTextPopover>
          {t(
            i18nLocale,
            'queryBuilder:rule.readonlyReferencePopover',
            'This filter can be applied only from the view by filtering away a single reference from the context menu.'
          )}
        </PlainTextPopover>
      }
    >
      {ruleWrapper}
    </WithUncontrolledPopover>
  );
};
